import { Box } from '@mui/material'
import { ReactComponent as SearchIcon } from 'assets/search.svg'
import AutocompleteInput from 'components/shared/textFields/AutocompleteInput'
import {
	excludeHealthyDiagnoses,
	sortByGroupTitle,
} from 'components/cattles/checkupPage/utils/checkupHistory'
import { schemaMatchesDiagnoses } from 'components/cattles/treatmentPage/utils/treatmentSchemaDisplay'
import { useGetTreatmentSchemas } from 'hook/query/useGetTreatmentSchemas'
import { ICheckupList, PositionHoof } from 'models'
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react'
import { fetchDiagnosesList } from 'store/cattlesSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { COLORS } from 'styles/colors'

interface Props {
	selectedHoof: PositionHoof
	setCheckupInfo: Dispatch<SetStateAction<ICheckupList>>
	checkupInfo: ICheckupList
	isSubmitAttempted: boolean
}

export const DiagnosisSelectSection = ({
	selectedHoof,
	setCheckupInfo,
	checkupInfo,
	isSubmitAttempted,
}: Props) => {
	const dispatch = useAppDispatch()
	const { diagnosesList, diagnosesListPending } = useAppSelector(
		state => state.cattlesSlice,
	)
	const { data: treatmentSchemas = [] } = useGetTreatmentSchemas()

	useEffect(() => {
		if (diagnosesList.length === 0) {
			dispatch(fetchDiagnosesList())
		}
	}, [dispatch, diagnosesList.length])

	const groupedDiagnoses = useMemo(
		() => sortByGroupTitle(excludeHealthyDiagnoses(diagnosesList)),
		[diagnosesList],
	)

	const handleSelectDiagnosis = (hoof: PositionHoof, ids: number[]) => {
		const selectedSickness = diagnosesList.filter(d => ids.includes(d.id))
		const allowedPlanIds = treatmentSchemas
			.filter(schema => schemaMatchesDiagnoses(schema, ids))
			.map(schema => schema.id)

		setCheckupInfo(prev => {
			if (ids.length === 0) {
				return {
					...prev,
					diagnosis: prev.diagnosis.filter(d => d.position !== hoof),
				}
			}

			const updated = prev.diagnosis.map(d =>
				d.position === hoof
					? {
							...d,
							sickness_id: ids,
							sickness: selectedSickness,
							treatment_plans_id: (d.treatment_plans_id || []).filter(id =>
								allowedPlanIds.includes(id),
							),
						}
					: d,
			)

			const exists = prev.diagnosis.some(d => d.position === hoof)

			return {
				...prev,
				diagnosis: exists
					? updated
					: [
							...prev.diagnosis,
							{
								position: hoof,
								sickness_id: ids,
								sickness: selectedSickness,
								comment: '',
							},
						],
			}
		})
	}

	const selectedDiagnoses = useMemo(() => {
		const findElement = checkupInfo.diagnosis.find(
			d => d.position === selectedHoof,
		)
		return findElement?.sickness_id || []
	}, [selectedHoof, checkupInfo.diagnosis])

	const showDiagnosisError =
		Boolean(isSubmitAttempted) && selectedDiagnoses.length === 0

	return (
		<Box sx={{ width: '100%' }}>
			<AutocompleteInput
				label='Выберите диагноз'
				widthForInput='100%'
				isMultiple
				isCheckbox
				needChip
				isShowTags={false}
				loading={diagnosesListPending}
				availableList={groupedDiagnoses}
				selectedValues={selectedDiagnoses}
				isError={showDiagnosisError}
				helperText='Обязательное поле'
				onClick={(id: number) => {
					const isSelected = selectedDiagnoses.includes(id)
					const newSelection = isSelected
						? selectedDiagnoses.filter(d => d !== id)
						: [...selectedDiagnoses, id]
					handleSelectDiagnosis(selectedHoof, newSelection)
				}}
				startIcon={<SearchIcon stroke={COLORS.icon} />}
			/>
		</Box>
	)
}
